import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { liveApi, buildLiveWsUrl } from "@/lib/liveApi";
import { Crown, Users, Lightning, CheckCircle, Stairs, ShieldCheck } from "@phosphor-icons/react";

/**
 * /live/:code/screen — Public Canvas for TV/projector/OBS.
 * Auto-refreshes state via WebSocket (falls back to REST on any drop).
 * Renders: lobby → question → reveal → scoreboard → ended.
 */
export default function PublicCanvas() {
  const { code } = useParams();
  const [event, setEvent] = useState(null);
  const [players, setPlayers] = useState([]);
  const [currentQ, setCurrentQ] = useState(null);
  const [answerCount, setAnswerCount] = useState(0);
  const [lastReveal, setLastReveal] = useState(null);
  const [error, setError] = useState("");
  const wsRef = useRef(null);

  useEffect(() => {
    let closed = false;
    let retryTimer = null;
    let retryDelay = 1000;

    const hydrate = () => liveApi.get(`/live/events/${code}`).then((r) => {
      setEvent(r.data.event);
      setPlayers(r.data.players || []);
      setCurrentQ(r.data.current_question || null);
    }).catch((e) => setError(e.response?.data?.detail || "Event not found"));

    const connect = () => {
      const ws = new WebSocket(buildLiveWsUrl({ code, role: "screen" }));
      wsRef.current = ws;
      ws.onopen = () => { retryDelay = 1000; };
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === "snapshot") {
            setEvent(msg.data.event); setPlayers(msg.data.players || []);
            setCurrentQ(msg.data.current_question || null);
          } else if (msg.type === "lobby_updated") {
            setPlayers(msg.data.players || []);
          } else if (msg.type === "state_changed" || msg.type === "event_config_updated") {
            setEvent(msg.data);
          } else if (msg.type === "question_started") {
            setCurrentQ(msg.data); setAnswerCount(0); setLastReveal(null);
          } else if (msg.type === "answers_progress") {
            setAnswerCount(msg.data.count);
          } else if (msg.type === "question_ended") {
            setCurrentQ((prev) => prev ? {
              ...prev, revealed: true,
              answer_index: msg.data.correct_index,
              accepted_answer: msg.data.correct_answer,
            } : prev);
            setPlayers(msg.data.scoreboard || []);
            setLastReveal(msg.data);
          } else if (msg.type === "scoreboard_updated") {
            setPlayers(msg.data.scoreboard || []);
          } else if (msg.type === "ladder_contestant_changed") {
            setEvent((prev) => prev ? { ...prev, ladder_active_contestant: msg.data.player_tempid } : prev);
          }
        } catch { /* ignore */ }
      };
      ws.onclose = () => {
        if (closed) return;
        retryTimer = setTimeout(() => {
          retryDelay = Math.min(retryDelay * 2, 10000);
          hydrate(); connect();
        }, retryDelay);
      };
    };

    hydrate(); connect();
    return () => {
      closed = true;
      if (retryTimer) clearTimeout(retryTimer);
      try { wsRef.current?.close(); } catch { /* noop */ }
    };
  }, [code]);

  if (error) return (
    <div className="min-h-screen bg-nk-bg text-nk-text flex items-center justify-center px-6">
      <div className="text-center">
        <p className="font-mono text-xs uppercase text-nk-gold/70 mb-2">NKognito Live</p>
        <p className="text-lg text-nk-bad" data-testid="canvas-error">{error}</p>
      </div>
    </div>
  );
  if (!event) return (
    <div className="min-h-screen bg-nk-bg text-nk-text flex items-center justify-center">
      <p className="text-sm text-nk-mute">Preparing the arena…</p>
    </div>
  );

  const joinUrl = `${window.location.origin}/live/${event.code}/play`;
  const showLobby = (event.state === "lobby") || (event.state === "running" && !currentQ);
  const showQuestion = event.state === "running" && currentQ;

  return (
    <div className="min-h-screen bg-nk-bg text-nk-text" data-testid="public-canvas">
      <header className="px-8 pt-8 flex items-start justify-between gap-6">
        <div className="flex items-center gap-4">
          {event.host_brand_image ? (
            <img src={event.host_brand_image} alt="" className="w-14 h-14 rounded-full object-cover border border-nk-line" />
          ) : (
            <div className="w-14 h-14 rounded-full bg-nk-gold flex items-center justify-center">
              <Crown size={26} weight="fill" className="text-nk-bg" />
            </div>
          )}
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-nk-gold/80">NKognito Live</p>
            <p className="text-sm text-nk-mute mt-0.5">Hosted by {event.host_display_name}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="font-mono text-[10px] uppercase tracking-widest text-nk-gold/60">Mode</p>
          <p className="text-sm">{event.mode.toUpperCase()} · {event.template}</p>
          <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-nk-gold/60">Code</p>
          <p className="text-4xl font-display tracking-widest text-nk-gold" data-testid="canvas-code">
            {event.code}
          </p>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-8 mt-10 pb-10">
        {event.state === "draft" && <StateBadge state="draft" text="Waiting for host to open the lobby…" />}
        {event.state === "ended" && <EndedPanel players={players} title={event.title} event={event} />}
        {showLobby && <LobbyPanel event={event} players={players} joinUrl={joinUrl} />}
        {showQuestion && event.mode === "ladder" && (
          <LadderPanel q={currentQ} answerCount={answerCount} playersCount={players.length}
                       scoreboard={players} lastReveal={lastReveal} />
        )}
        {showQuestion && event.mode !== "ladder" && (
          <QuestionPanel q={currentQ} answerCount={answerCount} playersCount={players.length}
                         scoreboard={players} lastReveal={lastReveal} />
        )}
      </main>
    </div>
  );
}


function StateBadge({ state, text }) {
  return (
    <div className="nk-card p-10 text-center border-dashed border-nk-line" data-testid={`canvas-state-${state}`}>
      <p className="font-mono text-[10px] uppercase tracking-widest text-nk-gold/70 mb-2">{state}</p>
      <p className="text-2xl font-display">{text}</p>
    </div>
  );
}


function EndedPanel({ players, title, event }) {
  const podium = [...players].sort((a, b) => (b.score || 0) - (a.score || 0)).slice(0, 3);
  const winner = podium[0];
  const isLadderCash = event?.mode === "ladder" && event.config?.ladder?.reward_type === "cash";
  const symbol = event?.config?.ladder?.currency
    ? { USD: "$", NGN: "₦", GHS: "₵", KES: "KSh", ZAR: "R" }[event.config.ladder.currency]
    : "$";
  const formatScore = (v) => isLadderCash ? `${symbol}${(v || 0).toLocaleString()}` : `${v || 0}`;
  return (
    <div className="text-center" data-testid="canvas-ended">
      <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-nk-gold/70">Event ended</p>
      <h1 className="font-display text-5xl mt-3 mb-8">{title}</h1>
      {winner && (
        <div className="inline-block nk-card p-8 border border-nk-gold">
          <Crown size={44} weight="fill" className="mx-auto text-nk-gold mb-3" />
          <p className="eyebrow text-nk-gold">Champion</p>
          <p className="text-4xl font-display mt-2">{winner.display_name}</p>
          <p className="text-sm text-nk-mute mt-1">{formatScore(winner.score)} {isLadderCash ? "" : "points"}</p>
        </div>
      )}
      {podium.length > 1 && (
        <ul className="mt-8 max-w-md mx-auto space-y-2 text-left">
          {podium.slice(1).map((p, i) => (
            <li key={p.player_tempid} className="nk-card p-3 flex items-center gap-3">
              <span className="font-mono text-nk-gold w-6">#{i + 2}</span>
              <span className="text-2xl">{p.avatar}</span>
              <span className="flex-1">{p.display_name}</span>
              <span className="font-mono text-sm text-nk-gold">{formatScore(p.score)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


function LobbyPanel({ event, players, joinUrl }) {
  return (
    <>
      <div className="text-center mb-10">
        <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-nk-gold/70">
          {event.state === "lobby" ? "Now boarding" : "Waiting for the first question"}
        </p>
        <h1 className="font-display text-6xl mt-3">{event.title}</h1>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        <section className="nk-card p-6 text-center" data-testid="canvas-join-card">
          <p className="font-mono text-[10px] uppercase tracking-widest text-nk-gold/70 mb-3">Scan to join</p>
          <div className="inline-block bg-white p-3 rounded-sm">
            <QRCodeSVG value={joinUrl} size={200} bgColor="#ffffff" fgColor="#0f0f10" />
          </div>
          <p className="text-xs text-nk-mute mt-4">or open</p>
          <p className="font-mono text-sm text-nk-gold mt-1 break-all">{joinUrl}</p>
        </section>
        <section className="lg:col-span-2 nk-card p-6" data-testid="canvas-players-card">
          <div className="flex items-center justify-between mb-4">
            <p className="font-mono text-[10px] uppercase tracking-widest text-nk-gold/70 inline-flex items-center gap-2">
              <Users size={12} weight="fill" /> In the room
            </p>
            <p className="font-mono text-sm text-nk-gold">{players.length}</p>
          </div>
          {players.length === 0 ? (
            <div className="py-12 text-center text-nk-mute">
              <Lightning size={26} weight="duotone" className="mx-auto text-nk-gold/50 mb-2" />
              <p className="text-sm">Waiting for players to join…</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-4">
              {players.map((p) => (
                <div className="text-center" key={p.player_tempid} data-testid={`canvas-player-${p.player_tempid}`}>
                  <div className="w-16 h-16 mx-auto rounded-full border-2 border-nk-gold/60 bg-nk-charcoal flex items-center justify-center text-3xl">
                    {p.avatar || "🎮"}
                  </div>
                  <p className="mt-2 text-xs text-nk-text truncate">{p.display_name}</p>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </>
  );
}


function QuestionPanel({ q, answerCount, playersCount, scoreboard, lastReveal }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (q.revealed) return undefined;
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [q.revealed, q.qid]);

  const startedMs = q.started_at ? new Date(q.started_at).getTime() : now;
  const limit = (q.time_limit_s || 20) * 1000;
  const remaining = Math.max(0, limit - (now - startedMs));
  const remainingS = Math.ceil(remaining / 1000);
  const pct = Math.max(0, Math.min(100, (remaining / limit) * 100));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6" data-testid="canvas-question">
      <section className="nk-card p-8">
        <div className="flex items-center justify-between mb-4">
          <p className="font-mono text-[10px] uppercase tracking-widest text-nk-gold/70">
            Q{q.index} of {q.total} · {q.category || "—"}
          </p>
          <p className="text-xs text-nk-mute font-mono">{answerCount}/{playersCount} answered</p>
        </div>
        <div className="h-1.5 bg-nk-line rounded-full overflow-hidden mb-6">
          <div className={`h-full transition-all duration-200 ${
            q.revealed ? "bg-nk-gold" : remainingS <= 5 ? "bg-nk-bad" : "bg-nk-gold"
          }`} style={{ width: `${q.revealed ? 100 : pct}%` }} />
        </div>
        {/* Emoji Phrase: enlarge the body font since it's just emojis */}
        {q.type === "emoji_phrase" ? (
          <div className="text-center mb-6" data-testid="canvas-emoji-body">
            <p className="font-mono text-[10px] uppercase text-nk-gold/70 mb-3">Guess the phrase</p>
            <p className="text-7xl md:text-8xl leading-tight">{q.body}</p>
          </div>
        ) : (
          <p className="font-display text-3xl md:text-4xl leading-tight mb-8" data-testid="canvas-question-body">
            {q.body}
          </p>
        )}

        {/* Literary Device: show the passage/snippet in a big blockquote */}
        {q.type === "literary_device" && q.snippet && (
          <blockquote className="mb-6 py-4 px-6 rounded-sm border-l-4 border-nk-gold bg-nk-gold/[0.06] italic text-2xl text-nk-text/90 max-w-3xl mx-auto"
                      data-testid="canvas-snippet">
            "{q.snippet}"
          </blockquote>
        )}

        {/* Unscramble letters — big centerpiece before options / input */}
        {q.type === "unscramble" && q.scrambled && (
          <div className="mb-8 py-6 px-4 rounded-sm border border-nk-gold/40 bg-nk-gold/[0.06] text-center"
               data-testid="canvas-scrambled">
            <p className="font-mono text-[11px] uppercase tracking-widest text-nk-gold/70 mb-3">Scrambled letters</p>
            <p className="font-display text-6xl tracking-[0.4em] text-nk-gold">{q.scrambled}</p>
          </div>
        )}

        {/* Word Bank chips (read-only visualization on the big screen) */}
        {q.type === "word_bank" && (q.word_bank || []).length > 0 && (
          <div className="mb-8 flex flex-wrap gap-3 justify-center" data-testid="canvas-word-bank">
            {(q.word_bank || []).map((w) => {
              const isCorrect = q.revealed && (q.accepted_answer || "").toLowerCase() === w.toLowerCase();
              return (
                <span key={w} className={`px-5 py-2.5 rounded-full text-lg font-medium border transition-all ${
                  isCorrect ? "border-nk-gold bg-nk-gold text-nk-bg scale-110" : "border-nk-line text-nk-text"
                }`}>{w}</span>
              );
            })}
          </div>
        )}

        {q.type === "multiple_choice" || !q.type ? (
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(q.options || []).map((o, i) => {
            const isCorrect = q.revealed && q.answer_index === i;
            const isWrong = q.revealed && q.answer_index !== i;
            return (
              <li key={i} data-testid={`canvas-option-${i}`}
                className={`px-4 py-4 rounded-sm border text-lg flex items-center gap-3 transition-all ${
                  isCorrect ? "border-nk-gold bg-nk-gold/15 text-nk-gold scale-[1.02]" :
                  isWrong ? "border-nk-line opacity-40" :
                  "border-nk-line"
                }`}
              >
                <span className="font-mono text-sm text-nk-mute w-5">{String.fromCharCode(65 + i)}.</span>
                <span className="flex-1">{o}</span>
                {isCorrect && <CheckCircle size={22} weight="fill" className="text-nk-gold" />}
              </li>
            );
          })}
        </ul>
        ) : (
        <div className="text-center py-6" data-testid="canvas-text-challenge">
          {!q.revealed ? (
            <p className="font-mono text-sm text-nk-mute uppercase tracking-widest">
              Players are typing…
            </p>
          ) : (
            <div className="inline-block px-8 py-5 rounded-sm border border-nk-gold bg-nk-gold/15">
              <p className="font-mono text-[10px] uppercase text-nk-gold/70 mb-1">Correct answer</p>
              <p className="font-display text-4xl text-nk-gold" data-testid="canvas-correct-answer">
                {q.accepted_answer}
              </p>
            </div>
          )}
        </div>
        )}
        {!q.revealed && (
          <p className="mt-6 text-center font-mono text-3xl text-nk-gold" data-testid="canvas-timer">
            {remainingS}s
          </p>
        )}
        {q.revealed && lastReveal?.winner_username && (
          <p className="mt-6 text-center text-sm text-nk-gold" data-testid="canvas-fastest">
            🏆 <b>{lastReveal.winner_username}</b> was fastest — +25 bonus
          </p>
        )}
      </section>

      <aside className="nk-card p-5" data-testid="canvas-scoreboard">
        <p className="font-mono text-[10px] uppercase tracking-widest text-nk-gold/70 mb-3">Scoreboard</p>
        <ul className="space-y-1.5">
          {scoreboard.slice(0, 12).map((p, i) => (
            <li key={p.player_tempid} className={`flex items-center gap-2 px-2 py-1.5 rounded-sm ${i === 0 ? "bg-nk-gold/10" : ""}`}>
              <span className="font-mono text-xs text-nk-gold w-5">#{i + 1}</span>
              <span className="text-lg">{p.avatar}</span>
              <span className="flex-1 text-sm truncate">{p.display_name}</span>
              <span className="font-mono text-sm text-nk-gold">{p.score || 0}</span>
            </li>
          ))}
        </ul>
      </aside>
    </div>
  );
}



function LadderPanel({ q, answerCount, playersCount, scoreboard, lastReveal }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (q.revealed) return undefined;
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [q.revealed, q.qid]);

  const startedMs = q.started_at ? new Date(q.started_at).getTime() : now;
  const limit = (q.time_limit_s || 20) * 1000;
  const remaining = Math.max(0, limit - (now - startedMs));
  const remainingS = Math.ceil(remaining / 1000);

  const L = q.ladder || {};
  const rungs = L.rungs || [];
  const currentRungIdx = L.rung_index ?? -1;
  const isCash = L.reward_type === "cash";
  const symbol = L.currency_symbol || "$";
  const fmt = (a) => isCash ? `${symbol}${(a || 0).toLocaleString()}` : `${a} pts`;

  const climbers = scoreboard.filter((p) => (p.ladder_current_rung ?? -1) >= 0);
  const rungForPlayer = (p) => p.ladder_current_rung ?? -1;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr_1fr] gap-6" data-testid="canvas-ladder">
      {/* LEFT — the ladder rendering */}
      <aside className="nk-card p-4 h-fit" data-testid="canvas-ladder-rungs">
        <div className="flex items-center gap-2 mb-3">
          <Stairs size={14} weight="fill" className="text-nk-gold" />
          <p className="font-mono text-[10px] uppercase tracking-widest text-nk-gold/70">Ladder</p>
        </div>
        <ul className="space-y-1">
          {[...rungs].map((r, idx) => idx).reverse().map((idx) => {
            const r = rungs[idx];
            const active = idx === currentRungIdx && !q.revealed;
            const cleared = idx < currentRungIdx || (q.revealed && idx === currentRungIdx);
            const climbersHere = climbers.filter((p) => rungForPlayer(p) === idx);
            return (
              <li
                key={idx}
                data-testid={`canvas-ladder-rung-${idx}`}
                className={`relative flex items-center gap-2 px-2 py-2 rounded-sm border transition-all ${
                  active ? "border-nk-gold bg-nk-gold/15 shadow-[0_0_20px_-8px_rgba(245,197,24,0.6)]" :
                  cleared ? "border-nk-gold/40 bg-nk-gold/5" :
                  "border-nk-line/40"
                }`}
              >
                <span className="font-mono text-[10px] text-nk-gold w-6">#{idx + 1}</span>
                <span className={`flex-1 font-mono text-sm ${active ? "text-nk-gold font-bold" : "text-nk-text"}`}>
                  {fmt(r.amount)}
                </span>
                {r.safe && (
                  <ShieldCheck size={13} weight="fill" className="text-nk-gold" title="Safe checkpoint" />
                )}
                {climbersHere.length > 0 && (
                  <span className="flex -space-x-1">
                    {climbersHere.slice(0, 4).map((p) => (
                      <span key={p.player_tempid}
                        title={p.display_name}
                        className={`text-base leading-none ${
                          p.ladder_eliminated ? "opacity-50 grayscale" :
                          p.ladder_walked_away ? "opacity-70" : ""
                        }`}>{p.avatar}</span>
                    ))}
                    {climbersHere.length > 4 && (
                      <span className="text-[10px] text-nk-mute">+{climbersHere.length - 4}</span>
                    )}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      </aside>

      {/* CENTER — the question */}
      <section className="nk-card p-8" data-testid="canvas-question">
        <div className="flex items-center justify-between mb-3">
          <p className="font-mono text-[10px] uppercase tracking-widest text-nk-gold/70">
            Rung {q.index} of {q.total} · {q.category || "—"}
          </p>
          <p className="text-xs text-nk-mute font-mono">{answerCount}/{playersCount} answered</p>
        </div>
        <div className="mb-4 px-4 py-3 rounded-sm border border-nk-gold/40 bg-nk-gold/10 flex items-center justify-between">
          <span className="font-mono text-[10px] uppercase text-nk-gold/70">This rung</span>
          <span className="font-display text-2xl text-nk-gold">{L.rung ? fmt(L.rung.amount) : ""}</span>
          {L.rung?.safe && (
            <span className="text-[10px] font-mono uppercase text-nk-gold inline-flex items-center gap-1">
              <ShieldCheck size={12} weight="fill" /> safe
            </span>
          )}
        </div>
        <p className="font-display text-2xl md:text-3xl leading-tight mb-8" data-testid="canvas-question-body">
          {q.body}
        </p>
        {q.type === "unscramble" && q.scrambled && (
          <div className="mb-6 py-4 px-3 rounded-sm border border-nk-gold/40 bg-nk-gold/[0.06] text-center"
               data-testid="canvas-scrambled-ladder">
            <p className="font-mono text-[10px] uppercase text-nk-gold/70 mb-2">Scrambled letters</p>
            <p className="font-display text-4xl tracking-[0.4em] text-nk-gold">{q.scrambled}</p>
          </div>
        )}
        {q.type === "multiple_choice" || !q.type ? (
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(q.options || []).map((o, i) => {
            const isCorrect = q.revealed && q.answer_index === i;
            const isWrong = q.revealed && q.answer_index !== i;
            return (
              <li key={i} data-testid={`canvas-option-${i}`}
                className={`px-4 py-4 rounded-sm border text-lg flex items-center gap-3 transition-all ${
                  isCorrect ? "border-nk-gold bg-nk-gold/15 text-nk-gold scale-[1.02]" :
                  isWrong ? "border-nk-line opacity-40" :
                  "border-nk-line"
                }`}
              >
                <span className="font-mono text-sm text-nk-mute w-5">{String.fromCharCode(65 + i)}.</span>
                <span className="flex-1">{o}</span>
                {isCorrect && <CheckCircle size={22} weight="fill" className="text-nk-gold" />}
              </li>
            );
          })}
        </ul>
        ) : (
        <div className="text-center py-6">
          {!q.revealed ? (
            <p className="font-mono text-sm text-nk-mute uppercase tracking-widest">Players are typing…</p>
          ) : (
            <div className="inline-block px-8 py-4 rounded-sm border border-nk-gold bg-nk-gold/15">
              <p className="font-mono text-[10px] uppercase text-nk-gold/70 mb-1">Answer</p>
              <p className="font-display text-3xl text-nk-gold">{q.accepted_answer}</p>
            </div>
          )}
        </div>
        )}
        {!q.revealed && (
          <p className="mt-6 text-center font-mono text-3xl text-nk-gold" data-testid="canvas-timer">
            {remainingS}s
          </p>
        )}
        {q.revealed && lastReveal?.ladder?.events && (
          <div className="mt-6 space-y-1" data-testid="canvas-ladder-events">
            {lastReveal.ladder.events.slice(0, 6).map((ev) => {
              const p = scoreboard.find((s) => s.player_tempid === ev.player_tempid);
              if (!p) return null;
              return (
                <p key={ev.player_tempid} className={`text-sm text-center ${
                  ev.outcome === "climb" ? "text-nk-gold" :
                  ev.outcome === "fell_to_safe" ? "text-nk-text" :
                  "text-nk-bad"
                }`}>
                  <span className="text-lg mr-1">{p.avatar}</span>
                  <b>{p.display_name}</b>
                  {" — "}
                  {ev.outcome === "climb" && `climbed to ${fmt(ev.amount)}`}
                  {ev.outcome === "fell_to_safe" && `fell back to safe ${fmt(ev.amount)}`}
                  {ev.outcome === "eliminated" && "eliminated"}
                </p>
              );
            })}
          </div>
        )}
      </section>

      {/* RIGHT — scoreboard */}
      <aside className="nk-card p-5 h-fit" data-testid="canvas-scoreboard">
        <p className="font-mono text-[10px] uppercase tracking-widest text-nk-gold/70 mb-3">Contestants</p>
        <ul className="space-y-1.5">
          {scoreboard.slice(0, 12).map((p, i) => (
            <li key={p.player_tempid} className={`flex items-center gap-2 px-2 py-1.5 rounded-sm ${
              i === 0 ? "bg-nk-gold/10" :
              p.ladder_eliminated ? "opacity-40" :
              p.ladder_walked_away ? "opacity-60" :
              ""
            }`}>
              <span className="font-mono text-xs text-nk-gold w-5">#{i + 1}</span>
              <span className="text-lg">{p.avatar}</span>
              <span className="flex-1 text-sm truncate">{p.display_name}</span>
              <span className="font-mono text-xs text-nk-gold">{fmt(p.score || 0)}</span>
            </li>
          ))}
        </ul>
      </aside>
    </div>
  );
}
